/* eslint-disable */
// /src/hooks/useDriverStatus.js

import { useEffect, useState } from "react";
import { getMemberKey } from "../services/authService";
import {
    findApprovalForMemberByNameAndCar,
    findApprovalForMemberByPhoneE164,
} from "../services/approvalsService";
import { fetchLatestMonthlyFareByFranchise } from "../services/settlementsService";

// ===== 세션 유틸 (HomePage와 동일 패턴) =====
const getSessionSafe = () => {
    if (typeof window === "undefined") return null;
    try {
        return JSON.parse(window.localStorage.getItem("dhm.session") || "null");
    } catch {
        return null;
    }
};

// 등급 계산: 운송매출 합계 기준 + 회원타입
// - memberType: "personal" | "corporate" | "general" | null
function computeGrade(totalFare, memberType = "personal") {
    const n = typeof totalFare === "number" ? totalFare : 0;
    if (!n || n <= 0) return "guest";

    const kind = memberType === "corporate" ? "corporate" : "personal";

    // 🔹 법인 기준
    if (kind === "corporate") {
        if (n >= 500000000) return "emerald"; // 5억 이상
        if (n >= 300000000) return "purple";  // 3억 ~ 5억 미만
        return "orange";                      // 1원 ~ 3억 미만
    }

    // 🔹 개인/일반 기준 (personal / general / 기타)
    if (n >= 10000000) return "emerald";    // 1,000만 이상
    if (n >= 6000000) return "purple";      // 600만 ~ 1,000만 미만
    return "orange";                        // 1원 ~ 600만 미만
}

/**
 * 전화번호(phoneE164) → 12자리 바코드 숫자
 * - 동일 phoneE164 에 대해서는 항상 같은 값
 * - 다른 phoneE164 이면 대부분 다른 값
 */
function makeMembershipBarcode(phoneE164) {
    const digits = String(phoneE164 || "")
        .replace(/\D/g, "") // 숫자만 추출
        .trim();

    if (!digits) return null;

    let h = 0;
    for (let i = 0; i < digits.length; i += 1) {
        h = (h * 131 + digits.charCodeAt(i)) % 10000000;
    }
    const hash7 = String(h).padStart(7, "0");

    const prefix = "91";
    const mid = "23";

    const base11 = prefix + mid + hash7; // 11자리
    const sum = base11.split("").reduce((acc, d) => acc + Number(d), 0);
    const check = String(sum % 10);      // 1자리

    return base11 + check;               // 12자리 문자열
}


export function useDriverStatus() {
    const [state, setState] = useState({
        loading: true,
        error: null,

        phoneE164: null,
        carNumber: null,
        name: null,
        username: null,

        // 🔥 아직 아무것도 모르는 상태(null)와
        // 🔥 진짜 게스트("guest")를 구분하기 위해 초기값은 null로 둠
        status: null,          // "guest" | "member" | null
        franchisename: null,
        memberType: null,      // "personal" | "corporate" | "general" | null

        totalFare: null,
        lastSettlementFare: null,
        lastSettlementMonth: null,
        grade: null,           // "guest" | "orange" | "purple" | "emerald" | null

        membershipBarcode: null,
    });

    const [version, setVersion] = useState(0); // 강제 리프레시용

    // 🔹 세션 기반 키: auth + phoneE164(or userId) 조합
    const session = getSessionSafe();
    const sessionKey = (() => {
        if (!session) return "guest:null";
        const auth = session.auth || "guest";
        const id =
            session.phoneE164 ||
            session.userId ||
            session.uid ||
            "";
        return `${auth}:${id}`;
    })();

    useEffect(() => {
        let cancelled = false;

        async function run() {
            console.groupCollapsed(
                `[useDriverStatus] run v${version} sessionKey=${sessionKey}`
            );
            setState((prev) => ({ ...prev, loading: true, error: null }));

            try {
                // 1) 현재 멤버 기본 키 가져오기
                const key = await getMemberKey();
                console.log("[useDriverStatus] memberKey =", key);

                // 🔹 로그인 안 되어 있거나 멤버가 없으면 → 진짜 게스트 확정
                if (!key || !key.phoneE164) {
                    if (cancelled) {
                        console.log(
                            "[useDriverStatus] cancelled before no-member state set"
                        );
                        console.groupEnd();
                        return;
                    }

                    console.log(
                        "[useDriverStatus] no memberKey → guest 상태로 초기화"
                    );
                    setState((prev) => ({
                        ...prev,
                        loading: false,
                        error: null,
                        phoneE164: null,
                        carNumber: null,
                        name: null,
                        username: null,
                        status: "guest",
                        franchisename: null,
                        memberType: null,
                        totalFare: null,
                        lastSettlementFare: null,
                        lastSettlementMonth: null,
                        grade: "guest",
                        membershipBarcode: null,
                    }));
                    console.groupEnd();
                    return;
                }

                const {
                    phoneE164,
                    carNumber,
                    name,
                    username,
                    memberType: memberTypeFromMember,
                } = key;

                // 2) approvals: 전화번호 기준으로 승인 정보 찾기
                console.log(
                    "[useDriverStatus] findApprovalForMemberByPhoneE164 params =",
                    { phoneE164 }
                );
                const approval = await findApprovalForMemberByPhoneE164(phoneE164);

                console.log(
                    "[useDriverStatus] approval (by phoneE164) result =",
                    approval
                );

                let status = "guest";
                let franchiseName = null;
                let memberType = memberTypeFromMember || null;

                if (approval && approval.franchiseName) {
                    status = "member";
                    franchiseName = approval.franchiseName;
                }

                // 3) 운송매출 합계 조회
                let totalFare = null;
                let lastSettlementFare = null;
                let lastSettlementMonth = null;

                if (status === "member" && franchiseName) {
                    try {
                        console.log(
                            "[useDriverStatus] fetchLatestMonthlyFareByFranchise params =",
                            { franchiseName }
                        );
                        const latest = await fetchLatestMonthlyFareByFranchise(
                            franchiseName
                        );
                        console.log(
                            "[useDriverStatus] latest monthly fare result =",
                            latest
                        );

                        if (latest && typeof latest === "object") {
                            const rawFare =
                                typeof latest.fare === "number"
                                    ? latest.fare
                                    : typeof latest.totalFare === "number"
                                        ? latest.totalFare
                                        : latest.fare;

                            const parsedFare =
                                typeof rawFare === "number"
                                    ? rawFare
                                    : Number(
                                        String(rawFare || "").replace(/[,\s]/g, "")
                                    ) || 0;

                            lastSettlementFare = parsedFare;
                            lastSettlementMonth =
                                latest.settlementMonth ||
                                latest.settlementMonthKey ||
                                null;
                            totalFare = parsedFare;
                        } else {
                            const parsedFare =
                                typeof latest === "number"
                                    ? latest
                                    : Number(
                                        String(latest || "").replace(/[,\s]/g, "")
                                    ) || 0;
                            lastSettlementFare = parsedFare;
                            lastSettlementMonth = null;
                            totalFare = parsedFare;
                        }
                    } catch (fareErr) {
                        console.error(
                            "[useDriverStatus] fetchLatestMonthlyFareByFranchise error",
                            fareErr
                        );
                    }
                } else {
                    console.log(
                        "[useDriverStatus] skip fare fetch: status=",
                        status,
                        "franchisename=",
                        franchiseName
                    );
                }

                // 4) 등급 계산
                const grade = computeGrade(totalFare, memberType || "personal");
                console.log("[useDriverStatus] computed grade =", grade);

                if (cancelled) {
                    console.log("[useDriverStatus] cancelled before state commit");
                    console.groupEnd();
                    return;
                }

                const membershipBarcode =
                    status === "member" && franchiseName
                        ? makeMembershipBarcode(franchiseName)
                        : null;

                const nextState = {
                    loading: false,
                    error: null,

                    phoneE164,
                    carNumber,
                    name,
                    username: username || null,

                    status,
                    franchisename: franchiseName,
                    memberType: memberType || null,

                    totalFare:
                        typeof totalFare === "number"
                            ? totalFare
                            : lastSettlementFare,
                    lastSettlementFare:
                        typeof lastSettlementFare === "number"
                            ? lastSettlementFare
                            : null,
                    lastSettlementMonth: lastSettlementMonth || null,
                    grade,
                    membershipBarcode,
                };

                console.log("[useDriverStatus] final state =", nextState);
                setState(nextState);
            } catch (e) {
                console.error("[useDriverStatus] error", e);
                if (cancelled) {
                    console.log("[useDriverStatus] cancelled after error");
                    console.groupEnd();
                    return;
                }
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    error:
                        e?.message ||
                        "드라이버 상태 조회 중 오류가 발생했습니다.",
                    status: "guest",
                    grade: "guest",
                    membershipBarcode: null,
                    lastSettlementFare: null,
                    lastSettlementMonth: null,
                }));
            } finally {
                console.groupEnd();
            }
        }

        run();
        return () => {
            cancelled = true;
        };
    }, [version, sessionKey]);

    const refresh = () => setVersion((v) => v + 1);

    return { ...state, refresh };
}
